import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { VaultDocument } from '@/types/vault';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

interface RenameDocumentParams {
  id: string;
  name: string;
}

const METHOD = 'PUT';
const ENDPOINT = 'document/:id/rename';
export const QUERY_KEY = (id: string) => getQueryKey(METHOD, ENDPOINT, { id });

export const useRenameDocument = (
  options: {
    onSuccess?: (data: VaultDocument) => void;
    onError?: (error: Error) => void;
  } = {}
) => {
  const queryClient = useQueryClient();
  const { session } = useAuth();

  return useMutation({
    mutationFn: async ({ id, name }: RenameDocumentParams) => {
      const data = await handleApiCall<VaultDocument, { name: string }>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, { id }),
        { name }
      );

      return data;
    },
    onSuccess: data => {
      // Invalidate vault documents query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['vaultDocuments'] });
      enqueueSnackbar('Document renamed successfully.', { variant: 'success' });

      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error: Error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
  });
};
